"use client";

import {
	Badge,
	Button,
	type DefaultLinkProps,
	Heading,
	type HeadingProps,
	Link,
	type LinkProps,
	Modal,
	PromotionalBadge,
	type PromotionalBadgeProps,
	RichText,
	Tag,
	type TagProps
} from "@pamperedchef/design-system";
import { className as classNameHelper } from "@/app/utilities/className";
import { objectHasValues } from "@/app/utilities/objectHasValues";
import { type MotionProps, motion } from "framer-motion";

import * as styles from "./OfferCard.module.scss";

export interface OfferCardProps<
	LinkComponentProps = DefaultLinkProps
> extends React.HTMLAttributes<HTMLDivElement> {
	audienceTag: TagProps;
	cardTitle: HeadingProps;
	cardDescription: string;
	promotionalBadge?: PromotionalBadgeProps;
	badgeText?: string;
	badgeCtaLabel?: string;
	badgeCtaModal?: {
		title: string;
		titleTag: HeadingProps["tag"];
		description: string;
	};
	cta?: LinkProps<LinkComponentProps>;
	motionProps?: MotionProps;
}

/**
 * Offer Card component
 */
export function OfferCard<LinkComponentProps = DefaultLinkProps>({
	audienceTag,
	cardTitle,
	cardDescription,
	promotionalBadge,
	badgeText,
	badgeCtaLabel,
	badgeCtaModal,
	cta,
	className,
	motionProps,
	...props
}: OfferCardProps<LinkComponentProps>): JSX.Element {
	const DivComponent = motionProps ? motion.div : "div";

	return (
		<DivComponent
			{...props}
			{...(motionProps as any)}
			{...classNameHelper(styles["container"], className)}
		>
			<div className={styles["content"]}>
				<div className={styles["content-text"]}>
					<Tag
						{...audienceTag}
						{...classNameHelper(
							styles["audience-tag"],
							audienceTag.className
						)}
					/>

					<Link
						{...cta}
						kind="tertiary"
						{...classNameHelper(
							styles["title"],
							cardTitle.className
						)}
					>
						<Heading {...cardTitle} />
					</Link>

					<RichText
						content={cardDescription}
						className={styles["description"]}
					/>

					{objectHasValues(promotionalBadge) && (
						<PromotionalBadge
							{...promotionalBadge}
							className={styles["promotional-badge"]}
							contentClassName={
								styles["promotional-badge__content"]
							}
						/>
					)}

					{badgeText && (
						<div className={styles["badge-container"]}>
							<Badge
								kind="default"
								className={styles["badge"]}
								icon={{ name: "Info" }}
								label={badgeText}
							/>
							{badgeCtaLabel && (
								<Modal
									size="md"
									title={badgeCtaModal?.title}
									triggerElement={
										<Button
											kind="tertiary"
											className={
												styles["see-details-trigger"]
											}
										>
											{badgeCtaLabel}
										</Button>
									}
								>
									<RichText
										className={styles["modal-text"]}
										content={
											badgeCtaModal?.description ?? ""
										}
									/>
								</Modal>
							)}
						</div>
					)}
				</div>

				{cta?.href && (
					<div className={styles["cta-container"]}>
						<Link
							{...cta}
							icon={{ name: "ChevronRight" }}
							kind="tertiary"
							size="md"
						/>
					</div>
				)}
			</div>
		</DivComponent>
	);
}
